/* REACT */
import { useState } from 'react';

/* PLUGINS */
import { Eye, EyeOff } from 'lucide-react';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

/* COMPONENTS */
import {
    FormControl,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface PasswordInputProps<TFieldValues extends FieldValues> {
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
    label: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    onInputChange?: () => void;
}

/**
 * DOCU: Reusable password input component with show/hide toggle functionality. <br>
 * Triggered: Used in login and registration forms for password fields. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @param field - React Hook Form field props.
 * @param label - Label text for the input.
 * @param placeholder - Optional placeholder text.
 * @param disabled - Whether the input is disabled.
 * @param required - Whether to show required asterisk.
 * @author Development Team
 */
export function PasswordInput<TFieldValues extends FieldValues>({
    field,
    label,
    placeholder = 'Enter your password',
    disabled = false,
    required = false,
    onInputChange,
}: PasswordInputProps<TFieldValues>) {
    const [show_password, setShowPassword] = useState(false);
    const input_id = `password-${field.name}`;

    return (
        <FormItem>
            <FormLabel htmlFor={input_id} className="block text-foreground mb-0 font-normal text-base">
                {label} {required && <span className="text-destructive" aria-hidden="true">*</span>}
            </FormLabel>
            <FormControl>
                <div className="relative">
                    <Input
                        id={input_id}
                        type={show_password ? 'text' : 'password'}
                        placeholder={placeholder}
                        disabled={disabled}
                        aria-required={required}
                        className="w-full px-4 py-3 border-2 border-border rounded-lg focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand transition-colors pr-12"
                        {...field}
                        onChange={(e) => {
                            field.onChange(e);
                            if (onInputChange) onInputChange();
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!show_password)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        disabled={disabled}
                        aria-label={show_password ? 'Hide password' : 'Show password'}
                    >
                        {show_password ? (
                            <EyeOff className="w-5 h-5" aria-hidden="true" />
                        ) : (
                            <Eye className="w-5 h-5" aria-hidden="true" />
                        )}
                    </button>
                </div>
            </FormControl>
            <FormMessage className="mt-1 text-sm text-destructive" role="alert" />
        </FormItem>
    );
}
